import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { SearchInput } from '@/components/forms/search-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { useAdminListControls } from '@/features/admin/shared/use-admin-list-controls'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { formatDateTime } from '@/features/admin/shared/format'
import { ApiError } from '@/lib/api/http-client'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import actionProbeIconUrl from '@/assets/action-menu/probe.svg'

import {
  deleteRegisteredModel,
  getModelProviderMeta,
  listRegisteredModels,
  runRegisteredModelManualProbe,
  type ModelProbeHistoryItem,
  type ModelProbeStatus,
  type ModelProbeSummary,
  type RegisteredModel,
} from './api'
import { AdminModelProviderFormDrawer } from './form-drawer'
import { ProviderLogo } from './provider-logo'

const pageSizeOptions = [10, 20, 50]
const defaultColumnIds = [
  'display-name',
  'provider',
  'status',
  'pricing',
  'created-at',
  'probe',
  'actions',
]
const statusValues = ['active', 'disabled'] as const
const probeHistoryStatusClassName: Record<ModelProbeStatus, string> = {
  failed: 'bg-[#f43f5e]',
  healthy: 'bg-[#10b981]',
  slow: 'bg-[#f59e0b]',
  unknown: 'bg-[#d7dae0]',
}
interface DrawerState {
  mode: 'create' | 'edit'
  modelId?: string
  open: boolean
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
    </svg>
  )
}

function formatProviderLabel(model: RegisteredModel) {
  return model.provider_label ?? model.provider.toUpperCase()
}

function formatPricingCurrencyPrefix(currency: string) {
  switch (currency.toUpperCase()) {
    case 'CNY':
      return '¥'
    case 'USD':
      return '$'
    default:
      return `${currency} `
  }
}

function formatPricing(model: RegisteredModel, emptyLabel: string) {
  if (
    !model.pricing_currency ||
    model.input_price_per_million == null ||
    model.output_price_per_million == null
  ) {
    return emptyLabel
  }

  const currencyPrefix = formatPricingCurrencyPrefix(model.pricing_currency)

  return `${currencyPrefix}${Number(model.input_price_per_million).toFixed(2)} / ${currencyPrefix}${Number(
    model.output_price_per_million,
  ).toFixed(2)}`
}

function formatProbeMilliseconds(value?: number | null) {
  return value == null ? '-' : `${String(value)}ms`
}

function formatProbeTps(value?: number | null) {
  return value == null ? '-' : value.toFixed(1)
}

function latestProbeStatus(summary?: ModelProbeSummary): ModelProbeStatus {
  return summary?.latest_result?.status ?? summary?.config?.latest_status ?? 'unknown'
}

function latestProbeLatency(summary?: ModelProbeSummary) {
  return summary?.latest_result?.latency_ms ?? summary?.config?.latest_latency_ms ?? null
}

function latestProbeTtft(summary?: ModelProbeSummary) {
  return summary?.latest_result?.ttft_ms ?? summary?.config?.latest_ttft_ms ?? null
}

function latestProbeThroughput(summary?: ModelProbeSummary) {
  return summary?.latest_result?.throughput_tps ?? summary?.config?.latest_throughput_tps ?? null
}

function PrimaryModelNameCell({ value }: { value: string }) {
  return (
    <span className="block max-w-[200px] truncate font-semibold text-[#1a1c19]" title={value}>
      {value}
    </span>
  )
}

function TextCell({ value }: { value: string }) {
  return (
    <span className="block max-w-[240px] truncate text-sm text-[var(--color-text)]" title={value}>
      {value}
    </span>
  )
}

function ProbeTimeline({ history }: { history?: ModelProbeHistoryItem[] }) {
  const placeholderItems: ModelProbeHistoryItem[] = Array.from({ length: 24 }, (_, index) => ({
    label: String(index + 1),
    status: 'unknown',
  }))
  const items: ModelProbeHistoryItem[] =
    history && history.length > 0 ? history.slice(-24) : placeholderItems

  return (
    <div aria-hidden className="flex h-4 items-center gap-[2px]">
      {items.map((item, index) => (
        <span
          className={['h-3 w-1 rounded-[2px]', probeHistoryStatusClassName[item.status]].join(' ')}
          key={`${item.label}-${String(index)}`}
        />
      ))}
    </div>
  )
}

function ProbeSummaryCell({
  checkedAtLabel,
  detailsLabel,
  detailsTitle,
  metricLabels,
  statusLabel,
  statusLabels,
  summary,
}: {
  checkedAtLabel: string
  detailsLabel: string
  detailsTitle: string
  metricLabels: {
    latency: string
    tps: string
    ttft: string
  }
  statusLabel: string
  statusLabels: Record<ModelProbeStatus, string>
  summary?: ModelProbeSummary
}) {
  const tooltipId = useId()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [popoverPosition, setPopoverPosition] = useState<{ left: number; top: number } | null>(null)
  const status = latestProbeStatus(summary)
  const latency = latestProbeLatency(summary)
  const ttft = latestProbeTtft(summary)
  const throughput = latestProbeThroughput(summary)
  const latestResult = summary?.latest_result
  const metrics = [
    { label: metricLabels.ttft, value: formatProbeMilliseconds(ttft) },
    { label: metricLabels.latency, value: formatProbeMilliseconds(latency) },
    { label: metricLabels.tps, value: formatProbeTps(throughput) },
  ]

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined
    }

    function updatePosition() {
      const trigger = triggerRef.current
      if (!trigger) {
        return
      }

      const rect = trigger.getBoundingClientRect()
      const width = 260
      const height = 146
      const left = Math.min(
        window.innerWidth - width - 12,
        Math.max(12, rect.left + rect.width / 2 - width / 2),
      )
      const preferredTop = rect.bottom + 8
      const top =
        preferredTop + height > window.innerHeight
          ? Math.max(12, rect.top - height - 8)
          : preferredTop

      setPopoverPosition({ left, top })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isPinned || typeof document === 'undefined') {
      return undefined
    }

    function closeOnOutsidePointer(event: PointerEvent) {
      const target = event.target
      if (target instanceof Node && triggerRef.current?.contains(target)) {
        return
      }

      setIsOpen(false)
      setIsPinned(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)

    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
    }
  }, [isPinned])

  function closeFloatingDetail() {
    setIsOpen(false)
    setIsPinned(false)
  }

  return (
    <>
      <button
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        aria-label={detailsLabel}
        className="block w-[142px] cursor-help rounded-sm px-0.5 py-1 outline-none transition-colors hover:bg-[rgba(37,99,235,0.06)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
        onBlur={() => {
          setIsOpen(false)
          setIsPinned(false)
        }}
        onClick={() => {
          const nextPinned = !isPinned
          setIsPinned(nextPinned)
          setIsOpen(nextPinned)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            closeFloatingDetail()
          }
        }}
        onMouseEnter={() => {
          if (!isPinned) {
            setIsOpen(true)
          }
        }}
        onMouseLeave={() => {
          if (!isPinned) {
            setIsOpen(false)
          }
        }}
        ref={triggerRef}
        type="button"
      >
        <ProbeTimeline history={summary?.history} />
      </button>
      {isOpen && popoverPosition && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="z-[120] w-[260px] rounded-md bg-[#1f2937] px-3 py-2.5 text-xs leading-5 text-white shadow-[0_14px_32px_rgba(15,23,42,0.22)]"
              id={tooltipId}
              role="tooltip"
              style={{ left: popoverPosition.left, position: 'fixed', top: popoverPosition.top }}
            >
              <div className="mb-2 flex items-center justify-between gap-3 border-b border-white/10 pb-1.5">
                <span className="font-semibold">{detailsTitle}</span>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/85">
                  {statusLabel}: {statusLabels[status]}
                </span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-white/65">{checkedAtLabel}</span>
                  <span className="font-medium tabular-nums">
                    {latestResult?.checked_at ? formatDateTime(latestResult.checked_at) : '-'}
                  </span>
                </div>
                {metrics.map((metric) => (
                  <div className="flex items-center justify-between gap-4" key={metric.label}>
                    <span className="text-white/65">{metric.label}</span>
                    <span className="font-medium tabular-nums">{metric.value}</span>
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

export function AdminModelProviderPage() {
  const { t } = useTranslation('adminModelProvider')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const {
    page,
    pageSize,
    search,
    searchInput,
    setPage,
    setPageSize,
    setSearchInput,
    setVisibleColumnIds,
    visibleColumnIds,
    visibleColumnIdSet,
  } = useAdminListControls({ defaultColumnIds })
  const [provider, setProvider] = useState('')
  const [status, setStatus] = useState('active')
  const [drawerState, setDrawerState] = useState<DrawerState>({ mode: 'create', open: false })
  const [deleteTarget, setDeleteTarget] = useState<RegisteredModel | null>(null)

  const metaQuery = useQuery({
    queryFn: getModelProviderMeta,
    queryKey: ['admin-model-provider-meta'],
  })

  const modelsQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listRegisteredModels({
        page,
        pageSize,
        provider,
        search,
        status,
      }),
    queryKey: ['admin-registered-models', { page, pageSize, provider, search, status }],
  })

  const providerOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allProviders'), value: '' },
      ...(metaQuery.data?.providers ?? []).map((item) => ({
        icon: (
          <ProviderLogo
            className="size-5"
            label={item.label}
            provider={item.key}
            variant="compact"
          />
        ),
        label: item.label,
        value: item.key,
      })),
    ],
    [metaQuery.data?.providers, t],
  )

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allStatuses'), value: '' },
      ...statusValues.map((item) => ({
        label: t(`status.${item}`),
        value: item,
      })),
    ],
    [t],
  )

  const deleteMutation = useMutation({
    mutationFn: (modelId: string) => deleteRegisteredModel(modelId),
    onError: (error) => {
      showFeedback({
        detail: error instanceof ApiError ? error.message : undefined,
        message: t('deleteDialog.failed'),
        scope: 'global',
        tone: 'error',
      })
    },
    onSuccess: () => {
      showFeedback({
        message: t('deleteDialog.success'),
        scope: 'global',
        tone: 'success',
      })
      setDeleteTarget(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-registered-models'] })
    },
  })

  const manualProbeMutation = useMutation({
    mutationFn: runRegisteredModelManualProbe,
    onError: (error) => {
      showFeedback({
        detail: error instanceof ApiError ? error.message : undefined,
        message: t('actions.probeFailed'),
        scope: 'global',
        tone: 'error',
      })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-registered-models'] })
    },
    onSuccess: (summary) => {
      const status = latestProbeStatus(summary)
      const isFailed = status === 'failed'
      const isSlow = status === 'slow'

      showFeedback({
        detail: isFailed ? summary.latest_result?.message : undefined,
        message: isFailed
          ? t('actions.probeFailed')
          : isSlow
            ? t('actions.probeSlow')
            : t('actions.probeSuccess'),
        scope: 'global',
        tone: isFailed ? 'error' : isSlow ? 'warning' : 'success',
      })
    },
  })
  const isManualProbePending = manualProbeMutation.isPending
  const runManualProbe = manualProbeMutation.mutate

  const columns = useMemo<DataTableColumn<RegisteredModel>[]>(
    () => [
      {
        header: t('table.publicModelName'),
        hidden: !visibleColumnIdSet.has('display-name'),
        id: 'display-name',
        pin: 'left',
        render: (model) => <PrimaryModelNameCell value={model.public_model_name} />,
        visibilityLabel: t('table.publicModelName'),
        width: 190,
      },
      {
        header: t('table.provider'),
        hidden: !visibleColumnIdSet.has('provider'),
        id: 'provider',
        render: (model) => (
          <div className="flex min-w-[120px] items-center gap-2">
            <ProviderLogo label={formatProviderLabel(model)} provider={model.provider} />
            <span className="truncate">{formatProviderLabel(model)}</span>
          </div>
        ),
        visibilityLabel: t('table.provider'),
        width: 150,
      },
      {
        header: t('table.actualModelName'),
        hidden: !visibleColumnIdSet.has('actual-name'),
        id: 'actual-name',
        render: (model) => <TextCell value={model.model_name} />,
        visibilityLabel: t('table.actualModelName'),
        width: 250,
      },
      {
        header: t('table.interface'),
        hidden: !visibleColumnIdSet.has('interface'),
        id: 'interface',
        render: (model) => (
          <Badge variant="neutral">{model.interface_label ?? model.interface_type}</Badge>
        ),
        visibilityLabel: t('table.interface'),
        width: 220,
      },
      {
        header: t('table.status'),
        hidden: !visibleColumnIdSet.has('status'),
        id: 'status',
        render: (model) => (
          <Badge variant={model.status === 'active' ? 'success' : 'danger'}>
            {t(`status.${model.status}`)}
          </Badge>
        ),
        visibilityLabel: t('table.status'),
        width: 90,
      },
      {
        header: t('table.pricing'),
        hidden: !visibleColumnIdSet.has('pricing'),
        id: 'pricing',
        render: (model) => (
          <span className="text-sm text-[var(--color-text)]">
            {formatPricing(model, t('table.noPricing'))}
          </span>
        ),
        visibilityLabel: t('table.pricing'),
        width: 145,
      },
      {
        header: t('table.createdAt'),
        hidden: !visibleColumnIdSet.has('created-at'),
        id: 'created-at',
        render: (model) => formatDateTime(model.created_at),
        visibilityLabel: t('table.createdAt'),
        width: 165,
      },
      {
        header: t('table.probe'),
        hidden: !visibleColumnIdSet.has('probe'),
        id: 'probe',
        pin: 'right',
        render: (model) => (
          <ProbeSummaryCell
            checkedAtLabel={t('probe.checkedAt')}
            detailsLabel={t('probe.detailsLabel')}
            detailsTitle={t('probe.detailsTitle')}
            metricLabels={{
              latency: t('probe.metrics.latency'),
              tps: t('probe.metrics.tps'),
              ttft: t('probe.metrics.ttft'),
            }}
            statusLabel={t('probe.statusLabel')}
            statusLabels={{
              failed: t('probe.status.failed'),
              healthy: t('probe.status.healthy'),
              slow: t('probe.status.slow'),
              unknown: t('probe.status.unknown'),
            }}
            summary={model.probe_summary}
          />
        ),
        visibilityLabel: t('table.probe'),
        width: 170,
      },
      {
        align: 'center',
        header: t('table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (model) => (
          <ActionMenu
            ariaLabel={t('actions.open', { name: model.public_model_name })}
            items={[
              {
                disabled: isManualProbePending,
                iconSrc: actionProbeIconUrl,
                key: 'probe',
                label: t('actions.probe'),
                onSelect: () => {
                  runManualProbe(model.id)
                },
              },
              {
                iconSrc: actionEditIconUrl,
                key: 'edit',
                label: t('actions.edit'),
                onSelect: () => {
                  setDrawerState({ mode: 'edit', modelId: model.id, open: true })
                },
              },
              {
                iconSrc: actionDeleteIconUrl,
                key: 'delete',
                label: t('actions.delete'),
                onSelect: () => setDeleteTarget(model),
                variant: 'danger',
              },
            ]}
          />
        ),
        visibilityLabel: t('table.actions'),
        width: 70,
      },
    ],
    [isManualProbePending, runManualProbe, t, visibleColumnIdSet],
  )

  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  const listData = modelsQuery.data
  const models = listData?.items ?? []
  const total = listData?.total ?? 0
  const showError = modelsQuery.isError && !modelsQuery.data
  const errorDescription =
    modelsQuery.error instanceof ApiError
      ? t('errorDescriptionWithDetail', { detail: modelsQuery.error.message })
      : t('errorDescription')

  return (
    <AdminShellPage activePath="/admin/model-provider">
      <section
        aria-label={t('workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-model-provider-workspace"
      >
        <PageHeader
          actions={
            <>
              <DataTableColumnSelector
                aria-label={t('columnSelector.label')}
                onValueChange={setVisibleColumnIds}
                options={columnSelectorOptions}
                value={visibleColumnIds}
              >
                {t('columnSelector.trigger')}
              </DataTableColumnSelector>
              <Button
                disabled={metaQuery.isError}
                onClick={() => setDrawerState({ mode: 'create', open: true })}
                size="sm"
              >
                <PlusIcon />
                {t('actions.add')}
              </Button>
            </>
          }
          description={t('description')}
          title={t('title')}
        />
        <FilterBar>
          <SearchInput
            aria-label={t('filters.searchLabel')}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            value={searchInput}
          />
          <Select
            aria-label={t('filters.providerLabel')}
            disabled={metaQuery.isLoading}
            onValueChange={(value) => {
              setProvider(value)
              setPage(1)
            }}
            options={providerOptions}
            value={provider}
          />
          <Select
            aria-label={t('filters.statusLabel')}
            onValueChange={(value) => {
              setStatus(value)
              setPage(1)
            }}
            options={statusOptions}
            value={status}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 lg:px-[30px]">
          {showError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => void modelsQuery.refetch()}
                  size="sm"
                  variant="secondary"
                >
                  {t('retry')}
                </Button>
              }
              description={errorDescription}
              title={t('errorTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('empty')}
                getRowKey={(model) => model.id}
                isLoading={modelsQuery.isLoading}
                rows={models}
              />
              <Pagination
                disabled={modelsQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                total={total}
              />
            </>
          )}
        </div>
        <AdminModelProviderFormDrawer
          meta={metaQuery.data}
          mode={drawerState.mode}
          modelId={drawerState.modelId}
          onOpenChange={(open) => {
            setDrawerState((current) => ({
              ...current,
              open,
            }))
          }}
          open={drawerState.open}
        />
        <DeleteModelDialog
          isSubmitting={deleteMutation.isPending}
          model={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => {
            if (deleteTarget) {
              deleteMutation.mutate(deleteTarget.id)
            }
          }}
          open={Boolean(deleteTarget)}
        />
      </section>
    </AdminShellPage>
  )
}

function DeleteModelDialog({
  isSubmitting,
  model,
  onClose,
  onConfirm,
  open,
}: {
  isSubmitting: boolean
  model?: RegisteredModel | null
  onClose: () => void
  onConfirm: () => void
  open: boolean
}) {
  const { t } = useTranslation('adminModelProvider')

  return (
    <Modal
      closeLabel={t('deleteDialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('deleteDialog.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onConfirm} size="sm" variant="danger">
            {t('deleteDialog.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      subtitle={t('deleteDialog.subtitle')}
      title={t('deleteDialog.title')}
    >
      <p className="text-sm leading-6 text-[var(--color-text)]">
        {t('deleteDialog.message', { name: model?.public_model_name ?? '' })}
      </p>
    </Modal>
  )
}
